<template>
	<TeamingDialog
		:class-name="classes"
		:show-dialog="showDialog"
		v-on="bubble( `hide` )"
	>
		<template #heading>
			{{ translate( "heading" ) }}
		</template>

		<template #footer>
			<div class="footer">
				<TeamingButton
					@click.stop="onSubmit"
				>
					{{ translate( "createButton" ) }}
				</TeamingButton>
				<TeamingButton
					:type="enums.TeamingButtonType.QUATERNARY"
					@click.stop="$emit( `cancel` )"
				>
					{{ translate( "cancelButton" ) }}
				</TeamingButton>
			</div>
		</template>

		<div class="body">
			<div class="body-row">
				<h2 class="sub-heading">{{ translate( "subHeading" ) }}</h2>
				<TeamingHint>{{ translate( "whoIsRelationshipWithHint" ) }}</TeamingHint>
			</div>

			<slot name="teammate">
				<div class="body-row">
					<LabeledInputField
						class="email-field"
						:hint="translate( `emailFieldHint` )"
						:placeholder="translate( `emailFieldPlaceholder` )"
						:label="translate( `emailFieldLabel` )"
						:value="teammateEmail"
						:disabled="teammateIsReadOnly"
						@input="onEmailInput"
						:is-valid="!errors.teammateEmail"
						:validation-error="{ message: errors.teammateEmail }"
					>
						<template #popunder>
							<TeammatePopunder
								class-name="teammate-popunder"
								:teammates="teammates"
								:is-visible="showTeammateSuggestions"
								@select="onSelectTeammate"
							/>
						</template>
					</LabeledInputField>
				</div>
			</slot>

			<div class="body-row">
				<LabeledDropDown
					class-name="role-field"
					:hint="translate( `roleFieldHint` )"
					:label="translate( `roleFieldLabel` )"
					:options="roleOptions"
					:value="teammateRole"
					@change="onRoleChange"
				/>
			</div>

			<!-- FEATUREFLAG -->
			<div v-if="$config().enableDeferOneOnOneInvitation" class="body-row defer-row">
				<div>
					<TeamingCheckbox
						class-name="defer-checkbox"
						name="new-oneonone-dialog-defer-checkbox"
						:is-checked="deferInvitation"
						@change="onToggleDefer"
					/>
				</div>
				<div>
					<h3 class="defer-title">{{ translate( "deferTitle" ) }}</h3>
					<p>{{ translate( "deferDescription" ) }}</p>
				</div>
			</div>
		</div>
	</TeamingDialog>
</template>

<script>
import {
	OneOnOneTeamRoleType
} from "@teaming/common-enums";

import {
	TranslateMixin,
	EventsMixin,
	EnumsMixin,
	TeamingDialog,
	TeamingButton,
	LabeledInputField,
	LabeledDropDown,
	TeamingCheckbox,
	TeamingHint,
	TeammatePopunder
} from "@teaming/common-ui";

export default {
	name: "NewOneOnOneDialog",
	teamingPackage: "common-team",
	mixins: [ EventsMixin, EnumsMixin, TranslateMixin, ],
	inject: [ "config", ],
	components: {
		LabeledDropDown,
		LabeledInputField,
		TeamingDialog,
		TeamingButton,
		TeamingHint,
		TeammatePopunder,
		TeamingCheckbox,
	},
	emits: [ "change", "hide", "submit", "cancel", ],
	props: {
		className: {
			type: String,
			default: "",
		},
		showDialog: {
			type: Boolean,
			required: true,
		},
		teammates: {
			type: Array,
			required: true,
		},
		showTeammateSuggestions: {
			type: Boolean,
			required: true,
		},
		teammateEmail: {
			type: String,
			required: true,
		},
		teammateRole: {
			type: String,
			required: true,
		},
		deferInvitation: {
			type: Boolean,
			required: true,
		},
		errors: {
			type: Object,
			default: () => {},
		},
		teammateIsReadOnly: {
			type: Boolean,
			default: false,
		},
		canValidate: {
			type: Boolean,
			required: true,
		},
	},
	computed: {
		classes() {
			return `new-oneonone-dialog ${ this.className }`.trim();
		},
		roleOptions() {
			return [
				{
					text: this.translateRoleType( OneOnOneTeamRoleType.MANAGER ),
					value: OneOnOneTeamRoleType.MANAGER,
				},
				{
					text: this.translateRoleType( OneOnOneTeamRoleType.REPORT ),
					value: OneOnOneTeamRoleType.REPORT,
				},
				{
					text: this.translateRoleType( OneOnOneTeamRoleType.PEER ),
					value: OneOnOneTeamRoleType.PEER,
				},
			];
		},
		form() {
			const { teammateEmail, teammateRole, deferInvitation, canValidate, } = this;
			return { teammateEmail, teammateRole, deferInvitation, canValidate, };
		},
	},
	methods: {
		translateRoleType( key, args = {} ) {
			return this.$t( `common-enums.OneOnOneTeamRoleType_${ key }`, args );
		},
		onEmailInput( e ) {
			const { form, } = this;
			this.$emit( "change", {
				...form,
				teammateEmail: e.target.value,
			} );
		},
		onRoleChange( e ) {
			const { form, } = this;
			this.$emit( "change", {
				...form,
				teammateRole: e.value,
			} );
		},
		onSelectTeammate( teammate ) {
			const { form, } = this;
			this.$emit( "change", {
				...form,
				teammateEmail: teammate.email,
			} );
		},
		onToggleDefer( { isChecked, } ) {
			const { form, } = this;
			this.$emit( "change", {
				...form,
				deferInvitation: isChecked,
			} );
		},
		onSubmit() {
			const { form, } = this;
			this.$emit( "submit", {
				...form,
				canValidate: true,
			} );
		},
	},
};
</script>

<style lang="scss" scoped>
$subHeadingColor: $color2-500;

.new-oneonone-dialog .footer {
	width: 100%;
	display: flex;
	justify-content: flex-start;
}

.new-oneonone-dialog .body {
	width: 100%;

	// NOTE: min-height is necessary to give the
	// dropdown vertical clearance when open
	min-height: 100vh;

	@include for-size('sm') {
		min-height: 40vh;
	}
}

.new-oneonone-dialog .body-row {
	position: relative;
	margin-top: 1rem;

	&:first-of-type {
		margin-top: 0;
	}
}

.new-oneonone-dialog .sub-heading {
	@include font-and-line-for(1);
	font-weight: 600;
	color: $subHeadingColor;
}

.new-oneonone-dialog .teammate-popunder {
	max-height: 5rem;
}

.new-oneonone-dialog .defer-row {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-content: flex-start;
	gap: 0.625rem;

	@include font-and-line-for(-1);

	h3 {
		@include font-and-line-for(0);
	}
}

.new-oneonone-dialog .defer-checkbox {
	@include font-and-line-for(0);
}

.new-oneonone-dialog .defer-title {
	font-weight: 500;
}
</style>
